/**
 * settlementCollision.ts — solid buildings + retaining walls for W2b settlements
 * (W2b-2.5 Task 2).
 *
 * The collision sibling of structures.ts. It derives the SAME building boxes and
 * wall strips (via the shared, pure {@link buildStructures}) and folds them into a
 * {@link BoxColliderField}, which answers the two spatial queries the game runs
 * against solid geometry — `resolve` (push a capsule out) and `segmentHit` (first
 * crossing fraction of a bolt/ship segment). Those queries, the bucket index and the
 * brute==hash oracle all live in the base class now (boxColliders.ts); this file owns
 * only the settlement-specific INGESTION.
 *
 * GEOMETRY. Every solid is an ORIENTED BOX in planet-local 3D:
 *   - a BUILDING is `buildStructures`' grounded box — base-surface centre, the parcel
 *     u-heading as its width axis, w×d footprint, rising `h` above the base;
 *   - a WALL point-pair is a THIN box — spanning the strip segment (width), one
 *     {@link WALL_CAP_R} to each side (the "capsule radius"), rising the run's `cutH`.
 * Unifying both as boxes lets one slab test serve resolve AND segmentHit.
 *
 * INDEX. Features fold in region-at-a-time via {@link SettlementColliders.addRegion}
 * (idempotent per region cell, keyed packCell — like SettlementIndex).
 *
 * SHARED-CODE DISCIPLINE: pmath only (hypot); distances use the spec-exact
 * Math.sqrt. No banned Math.* / `**` — the pmath-ban gate covers this file. The box
 * axes are built from the SAME pmath `tangentFrame` structures.ts encodes yaw in.
 *
 * BUILDINGS (F2 → walkable roofs): a building folds ONE standable box PER
 * module AABB of its procedural plan (`buildingPlan(...).collision`) — the
 * exact massing the DEFAULT-ON visuals bake — so every roof level is standable
 * at its true height. The old F2 whole-footprint slab's footprint slack
 * (record w×d walls beyond the drawn massing) and its tower-head band (a full
 * footprint of phantom solid at tower-top height) are both erased by the
 * per-module boxes. The legacy box kit (opt-out escape hatch
 * hl-proc-buildings=0) still renders record-h boxes and will mismatch —
 * accepted for the soak period, unchanged. buildingPlan is a pure
 * deterministic function of record fields, so server and client prediction
 * re-derive identical solids — no protocol change.
 */
import * as pmath from "../pmath.js";
import { packCell } from "./cells.js";
import type { CityGraph } from "./citygraph.js";
import { buildStructures, BUILDING_STRIDE } from "./structures.js";
import {buildRegionOutdoors,outdoorPlanetFrame} from './outdoors.js';
import {outdoorParts} from '../settlement/outdoors.js';
import {regionCivicBuildings,civicFrame} from './civics.js';
import { type BuildingPlan } from "../buildings/plan.js";
import { buildingProgramForRecord } from "../buildings/program.js";
import {frontierForRecord,frontierBoxes,frontierLocal,frontierSupport,recordFrame,type FrontierRecord} from './frontierBuildings.js';
import { LEVEL_H_M } from "../buildings/kit.js";
import type { PlanetSampler } from "../universe.js";
import type { SettlementIndex } from "./carveindex.js";
import { BoxColliderField, tangentFrame } from "./boxColliders.js";

// The query-side knobs moved to boxColliders.ts with the machinery that reads them.
// Re-exported here so every existing importer (and `export * from
// "./settlementCollision.js"` in worldgen/index.ts) keeps resolving them unchanged.
export { ROOF_STEP_M, MAX_PUSH_M, COLLIDER_MAX_M, BoxColliderField, type BoxSpec } from "./boxColliders.js";

// ── knobs (binding) ────────────────────────────────────────────────────────────

/** Retaining-wall half-thickness (metres) — the "capsule radius" of a wall box. */
export const WALL_CAP_R = 0.5;

// ── walkable-roof stair treads (WR-2) ────────────────────────────────────────────
// A `trim-stair` (buildings/plan.ts trimPass) bridges an inset upper storey down to
// the exposed lower roof one LEVEL_H_M below, so multi-level roofs are climbable on
// foot. We fold each one as a stack of STAIR_TREADS thin flat standable boxes.
//
// VERIFIED emission — buildings/plan.ts trimPass:397–399 (with the step-face scan at
// :386–388): a trim-stair's {x,z} is the UPPER-storey cell's step-face EDGE
// (cell centre + HALF toward the open side `s`); y = (L+1)·LEVEL_H_M = the LOWER
// exposed roof top (level L's roof); rot = SIDE_ROT[s], and side `s` points FROM the
// step face TOWARD the exposed lower roof (the neighbour with occ(L) && !occ(L+1)).
// The UPPER roof is one LEVEL_H_M higher, at y + LEVEL_H_M. Consequence for the
// tread run: the −side is the SOLID upper-cell column (its module box already spans
// [y, y+LEVEL_H_M]); the ramp therefore climbs ENTIRELY on the +side (over the lower
// roof), rising toward the upper-roof edge at the step face. `along` stays ≥ 0 and
// shrinks to ~0 as the tread height rises — a straight read of the emission above.
/** Treads per LEVEL_H_M stair (one climbable ladder rung each). 12 → 16 with
 *  the 4 m storey so the riser stays ≤ 0.26 m (4/16 = 0.25). */
export const STAIR_TREADS = 16;
/** Tread width across the run (m) — the kit Ramp Stair plank width
 *  (kit.ts:180, box([1.2, LEVEL_H_M, 0.3], …)). */
export const STAIR_TREAD_W_M = 1.2;
/** Kit tilt of the visual plank about x (kit.ts:180, box(…, [0.62, 0, 0])). */
const STAIR_TILT_RAD = 0.62;
/** Horizontal run of the tilted plank (kit.ts:180): a LEVEL_H_M plank leaned
 *  STAIR_TILT_RAD projects LEVEL_H_M·sin(0.62) ≈ 1.743 m — recomputed from the kit
 *  doc constants, not blind-hardcoded. */
export const STAIR_RUN_M = LEVEL_H_M * pmath.sin(STAIR_TILT_RAD);
/** rot → open side (buildings/plan.ts SIDE_ROT = [0,2,1,3], an involution, so the
 *  side→rot table inverts to itself). Side dirs: 0→+D, 1→−D, 2→+W, 3→−W (matching
 *  SIDE_DX/SIDE_DZ in plan.ts). */
const SIDE_FOR_ROT = [0, 2, 1, 3] as const;

/**
 * Solid buildings + retaining walls for a planet's grown regions. Fold materialized
 * regions in with {@link addRegion}; query with the inherited
 * {@link BoxColliderField.resolve} / {@link BoxColliderField.segmentHit}.
 */
export class SettlementColliders extends BoxColliderField {
  private readonly sampler: PlanetSampler;
  private readonly index: SettlementIndex;

  /** packCell(regionId) of every folded region — the idempotence guard. */
  private readonly added = new Set<number>();

  /** Interiors P1–P3: one packed volume per HOLLOW building, stride 30:
   *    0-2 volume centre (planet-local)   3-5 width axis W   6-8 depth axis D
   *    9 baseR   10 floorTopM   11 shell level count (1..4)
   *    12 lampStart   13 lampCount (into {@link lampFlat}, xyz triplets)
   *    14 + 4·L .. per-level bounds (L < 4): [W-offset of the level's occupied
   *    massing centre from the volume centre, D-offset, halfW, halfD].
   *  PER-LEVEL bounds (P3) matter twice: a shrunk upper storey must not read
   *  "indoors" while you stand on the open TERRACE beside it (rain/light/
   *  camera would gate on open air), and each level grounds on ITS OWN slab
   *  ({@link interiorFloorRadiusAt}). Queried by {@link indoorsAt}. */
  private readonly interiors: number[] = [];
  private readonly lampFlat: number[] = [];
  /** Authored halls have unequal ceiling heights and open courts. Exact room
   * volumes preserve those spaces without inventing full rectangular storeys. */
  private readonly civicInteriors:{center:number[];width:number[];depth:number[];up:number[];halfW:number;halfD:number;height:number;lamps:number[]}[]=[];
  /** Floats-per-volume of the {@link interiors} pack. */
  private static readonly VOL_STRIDE = 30;
  /** Max storeys carried in a volume's per-level bounds table. */
  private static readonly VOL_MAX_LEVELS = 4;

  constructor(sampler: PlanetSampler, index: SettlementIndex) {
    super(sampler.def.radius);
    this.sampler = sampler;
    this.index = index;
  }

  /** True once the region cell (packCell key) has been folded in. */
  has(cellKey: number): boolean {
    return this.added.has(cellKey);
  }

  /**
   * Fold one region's solids into the index. Idempotent per region cell: a second
   * call with the same regionId is a no-op, so a RegionService may re-materialize
   * (or re-adopt) freely. Derives building boxes and wall strips via the shared
   * {@link buildStructures} (heights carved because the sampler carries the index).
   */
  addRegion(g: CityGraph): void {
    const key = packCell(g.regionId);
    if (this.added.has(key)) return;
    this.added.add(key);

    // Fold buildings + PARCEL-PERIMETER retaining-wall runs. Edge-march wall runs
    // follow road centrelines (retained shoulders); folding them as solid would plant
    // walls mid-road and trap bodies, so they are EXCLUDED (the renderer still DRAWS
    // them; recorded W2b-2.5 decision). Parcel-perimeter retaining walls ARE solid.
    // (R1 D6, 2026-07-10) Perimeter CITY walls are removed — no city-wall fold.
    const { buildings, parcelWallStrips } = buildStructures(g, this.sampler, this.index);

    // Buildings: ONE standable box PER module AABB of the record's plan
    // (walkable roofs — see the header). The plan is rebuilt from the record's
    // own fields with the SAME input derivation as the client bake
    // (buildingMeshes.ts): out-of-range archetype → habitation, doorSide via
    // the shared planDoorSide(yaw, doorYaw).
    const nB = buildings.length / BUILDING_STRIDE;
    for (let i = 0; i < nB; i++) {
      const o = i * BUILDING_STRIDE;
      const frontier=frontierForRecord(buildings,o);
      if(frontier){this.foldFrontier(buildings,o,frontier);continue;}
      const bcx = buildings[o], bcy = buildings[o + 1], bcz = buildings[o + 2];
      const yaw = buildings[o + 3];
      // Interiors P1: uses with an interior template fold as a hollow walk-in
      // shell (record field 15 = parcel use). Same derivation as the client
      // bake — a one-sided interior is the bolt-through-wall bug class.
      const program=buildingProgramForRecord(buildings,o),plan=program.plan;
      const f = tangentFrame(bcx, bcy, bcz);
      const cyw = pmath.cos(yaw), syw = pmath.sin(yaw);
      // width axis = the parcel u-heading in the tangent frame; up = radial.
      const wxx = f.t1[0] * cyw + f.t2[0] * syw;
      const wxy = f.t1[1] * cyw + f.t2[1] * syw;
      const wxz = f.t1[2] * cyw + f.t2[2] * syw;
      this.foldBuildingPlan(bcx, bcy, bcz, f.up, wxx, wxy, wxz, plan);

      // Interiors P1–P3: register the hollow building as an indoors volume
      // (per-level bounds); fold the furnishing layer and record lamp anchors.
      if (plan.shell !== null && plan.shell.floors.length > 0) {
        // Per-level occupied massing bounds from the modules (includes the
        // stairwell cell, which has no floor slab but IS interior space).
        const nL = Math.min(SettlementColliders.VOL_MAX_LEVELS,
          plan.shell.multiFloor ? plan.levels : 1);
        const bx0 = new Array<number>(nL).fill(Infinity);
        const bx1 = new Array<number>(nL).fill(-Infinity);
        const bz0 = new Array<number>(nL).fill(Infinity);
        const bz1 = new Array<number>(nL).fill(-Infinity);
        for (const m of plan.modules) {
          if (m.level >= nL) continue; // dome crown / solid uppers
          const hw = (m.wU * plan.unit) / 2, hd = (m.dU * plan.unit) / 2;
          if (m.x - hw < bx0[m.level]) bx0[m.level] = m.x - hw;
          if (m.x + hw > bx1[m.level]) bx1[m.level] = m.x + hw;
          if (m.z - hd < bz0[m.level]) bz0[m.level] = m.z - hd;
          if (m.z + hd > bz1[m.level]) bz1[m.level] = m.z + hd;
        }
        const cx0 = (bx0[0] + bx1[0]) / 2, cz0 = (bz0[0] + bz1[0]) / 2;
        // depth = W × up (the bake's right-handed order — matches foldBuildingPlan).
        let dxx = wxy * f.up[2] - wxz * f.up[1];
        let dxy = wxz * f.up[0] - wxx * f.up[2];
        let dxz = wxx * f.up[1] - wxy * f.up[0];
        const dl = pmath.hypot(dxx, dxy, dxz) || 1;
        dxx /= dl; dxy /= dl; dxz /= dl;

        // P2: furnish. Same shared derivation as the bake; boxes are BASE-
        // relative, so they fold through the same frame as plan.collision.
        const ip = program.interior;
        const lampStart = this.lampFlat.length / 3;
        if (ip !== null) {
          for (const box of ip.collision) {
            this.pushBox(
              bcx + wxx * box.x + f.up[0] * box.y + dxx * box.z,
              bcy + wxy * box.x + f.up[1] * box.y + dxy * box.z,
              bcz + wxz * box.x + f.up[2] * box.y + dxz * box.z,
              f.up, wxx, wxy, wxz, box.w * 0.5, box.h, box.d * 0.5, box.wall === true);
          }
          for (const a of ip.anchors) {
            if (a.kind !== "lamp" && a.kind !== "hearth") continue;
            const base = a.level === 0 ? ip.floorTopM : a.level * LEVEL_H_M;
            const ay = base + (a.kind === "hearth" ? 0.9 : 1.8);
            this.lampFlat.push(
              bcx + wxx * a.x + f.up[0] * ay + dxx * a.z,
              bcy + wxy * a.x + f.up[1] * ay + dxy * a.z,
              bcz + wxz * a.x + f.up[2] * ay + dxz * a.z);
          }
        }
        const lampCount = this.lampFlat.length / 3 - lampStart;

        this.interiors.push(
          bcx + wxx * cx0 + dxx * cz0,
          bcy + wxy * cx0 + dxy * cz0,
          bcz + wxz * cx0 + dxz * cz0,
          wxx, wxy, wxz, dxx, dxy, dxz,
          buildings[o + 8], plan.shell.floorTopM, nL, lampStart, lampCount);
        for (let L = 0; L < SettlementColliders.VOL_MAX_LEVELS; L++) {
          if (L < nL && bx1[L] > bx0[L]) {
            this.interiors.push(
              (bx0[L] + bx1[L]) / 2 - cx0, (bz0[L] + bz1[L]) / 2 - cz0,
              (bx1[L] - bx0[L]) / 2, (bz1[L] - bz0[L]) / 2);
          } else {
            this.interiors.push(0, 0, 0, 0); // absent level: zero extents
          }
        }
      }
    }

    // Walls: unpack the [N, (x,y,z,cutH)×N] runs into per-point-pair thin boxes.
    // Only the parcel-perimeter retaining provenance folds this way.
    const foldWallRuns = (wallStrips: Float64Array): void => {
      let p = 0;
      while (p < wallStrips.length) {
        const N = wallStrips[p]; p++;
        for (let s = 0; s < N - 1; s++) {
          const o = p + s * 4;
          const ax = wallStrips[o], ay = wallStrips[o + 1], az = wallStrips[o + 2], cutA = wallStrips[o + 3];
          const bx = wallStrips[o + 4], by = wallStrips[o + 5], bz = wallStrips[o + 6], cutB = wallStrips[o + 7];
          const cutH = (cutA + cutB) * 0.5;
          if (cutH <= 0) continue;
          const mx = (ax + bx) * 0.5, my = (ay + by) * 0.5, mz = (az + bz) * 0.5;
          const fu = tangentFrame(mx, my, mz).up;
          // width axis = along the strip segment, with the radial component removed so
          // it lies in the tangent plane (endpoints sit at (near) the same level).
          let ex = bx - ax, ey = by - ay, ez = bz - az;
          const segLen = pmath.hypot(ex, ey, ez);
          if (segLen < 1e-6) continue;
          const rad = ex * fu[0] + ey * fu[1] + ez * fu[2];
          let wxx = ex - rad * fu[0], wxy = ey - rad * fu[1], wxz = ez - rad * fu[2];
          const wl = pmath.hypot(wxx, wxy, wxz);
          if (wl < 1e-6) continue;
          wxx /= wl; wxy /= wl; wxz /= wl;
          this.pushBox(mx, my, mz, fu, wxx, wxy, wxz, segLen * 0.5, cutH, WALL_CAP_R, true);
        }
        p += N * 4;
      }
    };
    foldWallRuns(parcelWallStrips); // terraced-platform retaining walls (solid)
    for(const c of regionCivicBuildings(g,this.sampler)){
      const f=civicFrame(g,this.sampler.def.radius,c),w=f.width,up=f.up,d=[w[1]*up[2]-w[2]*up[1],w[2]*up[0]-w[0]*up[2],w[0]*up[1]-w[1]*up[0]];
      for(const [boxes,standable] of [[c.plan.obstacles,false],[c.plan.floors,true]] as const)for(const b of boxes){
        const base=f.center.map((v,i)=>v+w[i]*b.center[0]+up[i]*(b.center[1]-b.size[1]/2)+d[i]*b.center[2]) as [number,number,number];
        this.addBox({base,up:up as [number,number,number],width:w as [number,number,number],halfWidth:b.size[0]/2,halfDepth:b.size[2]/2,height:b.size[1],standable});
      }
      const interior=c.plan.civicInterior;
      if(interior)for(const room of interior.rooms){
        const transform=(p:number[])=>f.center.map((v,i)=>v+w[i]*p[0]+up[i]*p[1]+d[i]*p[2]);
        const lamps=interior.lamps.filter(p=>Math.abs(p[0]-room.center[0])<room.size[0]/2&&Math.abs(p[2]-room.center[2])<room.size[2]/2).flatMap(transform);
        this.civicInteriors.push({center:transform([room.center[0],room.floor,room.center[2]]),width:w,depth:d,up,halfW:room.size[0]/2,halfD:room.size[2]/2,height:room.ceiling-room.floor,lamps});
      }
    }
    const outdoors=buildRegionOutdoors(g,this.sampler,buildings);
    for(const part of outdoorParts(outdoors))if(part.solid){
      const frame=outdoorPlanetFrame(g,this.sampler.def.radius,part);
      this.addBox({base:frame.center.map((v,i)=>v-frame.up[i]*part.size[1]/2) as [number,number,number],up:frame.up as [number,number,number],width:frame.width as [number,number,number],halfWidth:part.size[0]/2,height:part.size[1],halfDepth:part.size[2]/2,standable:part.floor===true});
    }
  }

  /**
   * Interiors P1: is planet-local point p inside a hollow building's ground
   * floor? Drives the client's indoors presentation (weather gate, interior
   * light dim, short camera boom) and, in P4, indoor-NPC parity. Height is
   * measured RADIALLY off the building's grounded baseR (field 8) — flat-slab
   * error over a ≤ 30 m footprint is centimetres. Linear scan with a cheap
   * squared-distance reject; hollow buildings are a subset of records, and the
   * volume list is per-planet, so this stays micro-scale per frame.
   */
  indoorsAt(x: number, y: number, z: number): number {
    const S = SettlementColliders.VOL_STRIDE;
    const arr = this.interiors;
    const pr = Math.sqrt(x * x + y * y + z * z);
    for (let i = 0; i < arr.length; i += S) {
      const dx = x - arr[i], dy = y - arr[i + 1], dz = z - arr[i + 2];
      const levels = arr[i + 11];
      const topH = levels * LEVEL_H_M;
      // Coarse reject on the level-0 bounds + full height.
      const hw0 = arr[i + 16], hd0 = arr[i + 17];
      const reach = hw0 * hw0 + hd0 * hd0 + topH * topH;
      if (dx * dx + dy * dy + dz * dz > reach) continue;
      const hRel = pr - arr[i + 9];
      if (hRel < 0 || hRel >= topH) continue; // strict top: roof-standing is OUTSIDE
      // P3: the STOREY the height falls in has its own lateral bounds — a
      // shrunk upper floor must not claim the open terrace beside it.
      const L = Math.min(levels - 1, Math.floor(hRel / LEVEL_H_M));
      const b = i + 14 + 4 * L;
      const hw = arr[b + 2], hd = arr[b + 3];
      if (hw <= 0) continue; // absent level record
      const aw = dx * arr[i + 3] + dy * arr[i + 4] + dz * arr[i + 5];
      if (aw < arr[b] - hw || aw > arr[b] + hw) continue;
      const ad = dx * arr[i + 6] + dy * arr[i + 7] + dz * arr[i + 8];
      if (ad < arr[b + 1] - hd || ad > arr[b + 1] + hd) continue;
      return i / S;
    }
    for(const [i,room] of this.civicInteriors.entries()){
      const dx=x-room.center[0],dy=y-room.center[1],dz=z-room.center[2];
      const h=dx*room.up[0]+dy*room.up[1]+dz*room.up[2];
      if(h<-.08||h>=room.height)continue;
      if(Math.abs(dx*room.width[0]+dy*room.width[1]+dz*room.width[2])>room.halfW||Math.abs(dx*room.depth[0]+dy*room.depth[1]+dz*room.depth[2])>room.halfD)continue;
      return arr.length/S+i;
    }
    return -1;
  }

  /**
   * P2/P3: the AUTHORITATIVE indoor ground radius at planet-local p, or null
   * when p is not inside an interior volume. The raised floor slab cannot
   * ground through standRadius (that query returns the HIGHEST standable top
   * — the roof — which the character's fall window rightly rejects from
   * inside), so clampToGround maxes this in directly: indoors, your feet are
   * on the STOREY'S OWN slab — level 0 at the spread-raised floorTop, level
   * L at its slab top L·H. Falling through a stairwell opening hands you the
   * lower floor naturally as hRel drops.
   */
  interiorFloorRadiusAt(x: number, y: number, z: number): number | null {
    const idx = this.indoorsAt(x, y, z);
    if (idx < 0) return null;
    const room=this.civicInteriors[idx-this.interiors.length/SettlementColliders.VOL_STRIDE];
    if(room){
      const h=(x-room.center[0])*room.up[0]+(y-room.center[1])*room.up[1]+(z-room.center[2])*room.up[2];
      return pmath.hypot(x-h*room.up[0],y-h*room.up[1],z-h*room.up[2]);
    }
    const o = idx * SettlementColliders.VOL_STRIDE;
    const baseR = this.interiors[o + 9];
    const pr = Math.sqrt(x * x + y * y + z * z);
    const levels = this.interiors[o + 11];
    const L = Math.min(levels - 1, Math.floor((pr - baseR) / LEVEL_H_M));
    return L <= 0 ? baseR + this.interiors[o + 10] : baseR + L * LEVEL_H_M;
  }

  /** P2: the interior light anchors (planet-local [x,y,z] triplets, flat) of
   *  indoors volume `idx` (an {@link indoorsAt} return). Empty when idx < 0. */
  interiorLampsAt(idx: number): number[] {
    const S = SettlementColliders.VOL_STRIDE;
    const room=this.civicInteriors[idx-this.interiors.length/S];
    if(room)return [...room.lamps];
    const o = idx * S;
    if (idx < 0 || o >= this.interiors.length) return [];
    const start = this.interiors[o + 12] * 3;
    const n = this.interiors[o + 13] * 3;
    return this.lampFlat.slice(start, start + n);
  }

  /**
   * Fold one building's plan: one standable box per module AABB (walkable
   * roofs). Plan-local frame: x along the record width axis W, y up
   * (LocalBox.y is the box BASE offset — plan.ts collisionBoxes), z along
   * D = W × up — the RIGHT-HANDED client-bake order (buildingMeshes.ts:
   * "depth = width × up"), NOT pushBox's internal up×W: that sign only orients
   * the symmetric box slab, but the CENTRE offset along box.z must match the
   * bake or every off-axis module would fold mirrored in z. Task 2 (stair
   * treads) folds INSIDE this helper, after the module-box loop.
   */
  private foldFrontier(buildings:Float64Array,o:number,f:FrontierRecord):void {
    const frame=recordFrame(buildings,o),{width,up,depth}=frame;
    const groups=[{boxes:frontierBoxes(f,f.plan.obstacles),standable:false},
      {boxes:[...frontierBoxes(f,[...f.plan.floors,...f.plan.roofs??[]]),...frontierSupport(f)],standable:true}];
    for(const {boxes,standable} of groups)for(const b of boxes){
      const base=frame.point([b.center[0],b.center[1]-b.size[1]/2,b.center[2]]);
      this.addBox({base,up,width,halfWidth:b.size[0]/2,halfDepth:b.size[2]/2,height:b.size[1],standable});
    }
    // Register individual enclosed ground-floor rectangles. Porches and
    // workshops' yards remain outdoors; the L-shaped recess remains air.
    if(f.plan.cottage||f.plan.workplace)for(const floor of f.plan.floors){
      if(floor.center[2]>1.6||floor.center[1]>.5||floor.size[0]<2||floor.size[2]<2)continue;
      const local=frontierLocal(f,[floor.center[0],floor.center[1]+floor.size[1]/2,floor.center[2]]);
      const box=frontierBoxes(f,[floor])[0];
      this.civicInteriors.push({center:frame.point(local),width,depth,up,halfW:Math.max(0,box.size[0]/2-.25),halfD:Math.max(0,box.size[2]/2-.25),height:3.2,lamps:[]});
    }
  }

  private foldBuildingPlan(
    bcx: number, bcy: number, bcz: number,
    up: number[],
    wxx: number, wxy: number, wxz: number,
    plan: BuildingPlan,
  ): void {
    let dxx = wxy * up[2] - wxz * up[1];
    let dxy = wxz * up[0] - wxx * up[2];
    let dxz = wxx * up[1] - wxy * up[0];
    const dl = pmath.hypot(dxx, dxy, dxz) || 1;
    dxx /= dl; dxy /= dl; dxz /= dl;
    for (const box of plan.collision) {
      // `wall: true` shell boxes fold NON-standable (isWall) — a standable
      // wall panel would be walk-through in its top ROOF_STEP_M band.
      this.pushBox(
        bcx + wxx * box.x + up[0] * box.y + dxx * box.z,
        bcy + wxy * box.x + up[1] * box.y + dxy * box.z,
        bcz + wxz * box.x + up[2] * box.y + dxz * box.z,
        up, wxx, wxy, wxz, box.w * 0.5, box.h, box.d * 0.5, box.wall === true);
    }

    // Walkable-roof stair treads (WR-2): fold each `trim-stair` as STAIR_TREADS thin
    // flat standable boxes climbing the LEVEL_H_M rise, so multi-level roofs are
    // climbable on foot. Reuses this helper's frame — x along W, y up, z along
    // D = W × up (dxx.. above, the corrected bake sign) — exactly as the module
    // boxes. See the constants header for the VERIFIED trimPass emission
    // (buildings/plan.ts:397–399): t.{x,z} = the upper cell's step-face edge, t.y =
    // the LOWER roof top, `side` (from t.rot) points toward the exposed lower roof.
    // `along` therefore stays ≥ 0 (over the lower roof) and shrinks to ~0 (the step
    // face) as the tread base rises to t.y + LEVEL_H_M − riser — the top tread's top
    // landing flush with the upper roof. Going −side would bury treads inside the
    // SOLID upper-cell column (its module box spans [t.y, t.y + LEVEL_H_M]).
    const riser = LEVEL_H_M / STAIR_TREADS;
    const run = STAIR_RUN_M / STAIR_TREADS;
    for (const t of plan.trims) {
      if (t.piece !== "trim-stair") continue;
      const side = SIDE_FOR_ROT[t.rot];
      const sW = side === 2 ? 1 : side === 3 ? -1 : 0; // side dir along W
      const sD = side === 0 ? 1 : side === 1 ? -1 : 0; // side dir along D
      for (let k = 0; k < STAIR_TREADS; k++) {
        const ty = t.y + k * riser;                                 // tread base height
        const along = STAIR_RUN_M * (1 - (k + 0.5) / STAIR_TREADS); // +side; → step face as k rises
        const lx = t.x + sW * along, lz = t.z + sD * along;
        this.pushBox(
          bcx + wxx * lx + up[0] * ty + dxx * lz,
          bcy + wxy * lx + up[1] * ty + dxy * lz,
          bcz + wxz * lx + up[2] * ty + dxz * lz,
          up, wxx, wxy, wxz,
          (side >= 2 ? run : STAIR_TREAD_W_M) * 0.5, // hw: run along W, else tread width
          riser,
          (side >= 2 ? STAIR_TREAD_W_M : run) * 0.5, // hd: tread width along D, else run
          false);
      }
    }
  }
}
